// Cyrqle "Andy" chat API client.
// POST /api/chat: { messages, session, debug } → { reply, session, intent, knowledge }
// Public, no auth, CORS open.

const BASE = process.env.REACT_APP_CHAT_API_URL || "https://andy.sentinelzone.com";

export class ChatApiError extends Error {
  constructor(message, { code, detail } = {}) {
    super(message);
    this.name = "ChatApiError";
    this.code = code || "unknown";
    this.detail = detail || null;
  }
}

/**
 * Send a chat message to Andy.
 * @param {Array<{role:"user"|"assistant", content:string}>} messages — full conversation
 * @param {object} session — opaque server-managed session object (pass back what server returned)
 * @returns {Promise<{ reply: string, session: object, intent: string, knowledge: any[] }>}
 */
export async function sendChat(messages, session = {}, { signal } = {}) {
  if (!Array.isArray(messages) || messages.length === 0) {
    throw new ChatApiError("No messages", { code: "no_messages" });
  }
  const body = {
    messages,
    session: {
      user_id: session.user_id ?? null,
      analysis: session.analysis ?? null,
      analysis_id: session.analysis_id ?? null,
      turn: session.turn ?? 0,
      last_search: session.last_search ?? [],
      last_intent: session.last_intent ?? null,
    },
    debug: false,
  };

  let response;
  try {
    response = await fetch(`${BASE}/api/chat`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(body),
      signal,
    });
  } catch (err) {
    if (err.name === "AbortError")
      throw new ChatApiError("Aborted", { code: "aborted" });
    throw new ChatApiError("Network error", { code: "network", detail: err.message });
  }

  let data;
  try {
    data = await response.json();
  } catch (e) {
    throw new ChatApiError("Invalid response", { code: "invalid_response" });
  }

  if (!response.ok) {
    throw new ChatApiError(data?.error || "Chat failed", {
      code: "chat_failed",
      detail: data,
    });
  }

  return {
    reply: data.reply || "",
    session: data.session || {},
    intent: data.intent || null,
    knowledge: Array.isArray(data.knowledge) ? data.knowledge : [],
  };
}
